( function() {
	'use strict';

	// Live list of slider roots — re-queried whenever ProductQuery (or any other
	// AJAX module) replaces the cards inside a `.product-slider .swiper`.
	let sliders = Array.from( document.querySelectorAll( '.product-slider .swiper' ) );

	// We intentionally do NOT early-return when no sliders are present at boot:
	// the page may add or replace `.product-slider` elements asynchronously
	// (e.g. ProductQuery filter-change), and we still want to listen for that.

	// Track instances per element.
	const sliderMap = new Map();
	const mqMobile = window.matchMedia( '(max-width: 748px)' );
	const shouldStack = ( el ) => {
		const root = el.closest( '.product-slider' );
		return !!root && root.classList.contains( 'stack-on-mobile' ) && mqMobile.matches;
	};

	const debounce = ( fn, ms = 150 ) => {
		let t;
		return ( ...args ) => {
			clearTimeout( t );
			t = setTimeout( () => fn( ...args ), ms );
		};
	};

	const isVisible = ( el ) => {
		if ( !el ) {
			return false;
		}
		return !!( el.offsetWidth || el.offsetHeight || el.getClientRects().length );
	};

	const setVisible = ( el, show ) => {
		el.style.display = show ? '' : 'none';
	};

	const appendStyleOnce = ( href ) => {
		if ( document.head.querySelector( `link[href="${href}"]` ) ) {
			return;
		}
		const link = document.createElement( 'link' );
		link.rel = 'stylesheet';
		link.type = 'text/css';
		link.href = href;
		document.head.appendChild( link );
	};

	const loadScript = ( src ) =>
		new Promise( ( resolve, reject ) => {
			const existing = document.querySelector( `script[src="${src}"]` );
			if ( existing ) {
				if ( existing.dataset.loaded === 'true' || typeof window.Swiper !== 'undefined' ) {
					resolve();
					return;
				}
				existing.addEventListener( 'load', () => resolve(), { once: true } );
				existing.addEventListener( 'error', () => reject( new Error( `Failed to load ${src}` ) ), { once: true } );
				return;
			}
			const script = document.createElement( 'script' );
			script.src = src;
			script.async = true;
			script.addEventListener( 'load', () => {
				script.dataset.loaded = 'true';
				resolve();
			}, { once: true } );
			script.addEventListener( 'error', () => reject( new Error( `Failed to load ${src}` ) ), { once: true } );
			document.head.appendChild( script );
		} );

	function destroyOne( el ) {
		const inst = sliderMap.get( el );
		if ( inst && !inst.destroyed ) {
			inst.destroy( true, true );
		}
		sliderMap.delete( el );
	}

	function initialIndexFor( el ) {
		const inst = sliderMap.get( el );
		if ( !inst || inst.destroyed ) {
			return 0;
		}
		return inst.params?.loop ? inst.realIndex : inst.activeIndex;
	}

	function createOne( el, initialSlide = 0 ) {
		const countSlides = el.querySelectorAll( '.card' ).length;
		if ( countSlides <= 1 ) {
			return;
		}

		if ( shouldStack( el ) ) {
			destroyOne( el );
			return null;
		}

		let isConstrained = false;
		let container = el.closest( '.is-slider-container' );
		if ( !container ) {
			const maybeColumns = el.closest( '.wp-block-columns' );
			if (
				maybeColumns &&
				maybeColumns.parentElement &&
				maybeColumns.parentElement.classList.contains( 'wp-block-colibri-tabs__panel' )
			) {
				container = maybeColumns;
			}
		}
		isConstrained = !!container;

		const colWidth = isConstrained ? ( 1400 - 34 - 32 ) / 3 : 0;
		const sliderRoot = el.closest( '.product-slider' );
		const slidesPerView = Number( sliderRoot?.dataset.columnCount || 0 );
		const sliderAlignment = sliderRoot?.dataset.sliderAlignment;

		if ( !el.querySelector( '.swiper-button-next' ) ) {
			const wrapper = el.querySelector( '.swiper-wrapper' );
			if ( wrapper && wrapper.parentNode ) {
				const next = document.createElement( 'div' );
				next.className = 'swiper-button-next';
				const prev = document.createElement( 'div' );
				prev.className = 'swiper-button-prev';
				wrapper.parentNode.insertBefore( next, wrapper.nextSibling );
				wrapper.parentNode.insertBefore( prev, next.nextSibling );
			}
		}

		let breakpoints = null;
		switch ( Math.min( slidesPerView, countSlides ) ) {
			case 2:
				breakpoints = {
					[ 375 + colWidth ]: { slidesPerView: 1.2, spaceBetween: 16 },
					[ 730 + colWidth ]: { slidesPerView: Math.min( 2.2, countSlides ), spaceBetween: 24 },
					[ 1200 + colWidth ]: { slidesPerView: Math.min( 3.2, slidesPerView ), spaceBetween: 32 },
				};
				break;
			case 3:
				breakpoints = isConstrained
					? {
						1024: { slidesPerView: Math.min( 2.2, countSlides ), spaceBetween: 24 },
						1350: { slidesPerView: Math.min( 3.2, countSlides ), spaceBetween: 32 },
					}
					: {
						[ 375 + colWidth ]: { slidesPerView: 1.2, spaceBetween: 16 },
						[ 730 + colWidth ]: { slidesPerView: Math.min( 2.2, countSlides ), spaceBetween: 24 },
						[ 980 + colWidth ]: { slidesPerView: Math.min( 3.2, countSlides ), spaceBetween: 32 },
					};
				break;
			case 4:
				breakpoints = {
					[ 375 + colWidth ]: { slidesPerView: 1.2, spaceBetween: 16 },
					[ 730 + colWidth ]: { slidesPerView: Math.min( 2.2, countSlides ), spaceBetween: 24 },
					[ 980 + colWidth ]: { slidesPerView: Math.min( 3.2, countSlides ), spaceBetween: 32 },
					[ 1168 + colWidth ]: { slidesPerView: Math.min( 4.2, countSlides ), spaceBetween: 32 },
				};
				break;
			default:
				breakpoints = {
					[ 375 + colWidth ]: { slidesPerView: 1.2 },
				};
		}

		function toggleFades( s ) {
			const elNode = s.el;
			elNode.classList.toggle( 'is-start', !s.isLocked && s.isBeginning );
			elNode.classList.toggle( 'is-end', !s.isLocked && s.isEnd );
		}

		const args = {
			slidesPerView: 1.2,
			spaceBetween: 16,
			centeredSlides: false,
			centeredSlidesBounds: true,
			centerInsufficientSlides: 'left' !== sliderAlignment,
			loop: false,
			watchOverflow: true,
			freeMode: false,
			slidesPerGroup: 1,
			longSwipes: true,
			longSwipesRatio: 0.2,
			longSwipesMs: 120,
			resistanceRatio: 0.65,
			keyboard: { enabled: true },
			breakpoints,
			grabCursor: true,
			resistance: false,
			allowTouchMove: true,
			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
			},
			watchSlidesProgress: true,
			initialSlide,
			on: {
				breakpoint() {
					this.updateSlidesClasses();
				},
				slideChange: ( s ) => toggleFades( s ),
				afterInit: ( s ) => toggleFades( s ),
				reachBeginning: ( s ) => toggleFades( s ),
				reachEnd: ( s ) => toggleFades( s ),
				fromEdge: ( s ) => toggleFades( s ),
				resize: ( s ) => toggleFades( s ),
				lock: ( s ) => toggleFades( s ),
				unlock: ( s ) => toggleFades( s ),
			},
		};

		const instance = new Swiper( el, args );
		sliderMap.set( el, instance );
		return instance;
	}

	function initSliders() {
		sliders.forEach( ( el, index ) => {
			el.classList.add( `swiper-${index}` );
			if ( !sliderMap.has( el ) ) {
				createOne( el, 0 );
			}
		} );
	}

	function destroyAll() {
		Array.from( sliderMap.keys() ).forEach( destroyOne );
	}

	function recreateSliders() {
		const indices = new Map();
		sliders.forEach( ( el ) => {
			indices.set( el, initialIndexFor( el ) );
		} );

		destroyAll();
		sliders.forEach( ( el ) => {
			const idx = indices.get( el ) || 0;
			createOne( el, idx );
		} );

		requestAnimationFrame( () => {
			Array.from( sliderMap.values() ).forEach( ( s ) => s.update() );
			equalizeSlidesHeight();
		} );
	}

	const SWIPER_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.js';
	const SWIPER_STYLE_URL = 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.css';

	let swiperLoadPromise = null;
	function ensureSwiperLoaded() {
		if ( typeof window.Swiper !== 'undefined' ) {
			return Promise.resolve( true );
		}
		if ( swiperLoadPromise ) {
			return swiperLoadPromise;
		}
		appendStyleOnce( SWIPER_STYLE_URL );
		swiperLoadPromise = loadScript( SWIPER_SCRIPT_URL )
			.then( () => true )
			.catch( ( error ) => {
				console.error( error );
				swiperLoadPromise = null;
				return false;
			} );
		return swiperLoadPromise;
	}

	// Triggered when ProductQuery (or any other AJAX module) replaces the
	// cards inside a `.product-slider .swiper`. The cached slider DOM nodes
	// may still exist (when the .swiper-wrapper is preserved) or may have
	// been swapped out entirely (when the wrapper was rebuilt). Either way
	// we destroy stale Swiper instances, re-discover slider roots, and
	// re-initialize so navigation/breakpoints/SEO data all stay in sync.
	async function refreshSliders() {
		const ok = await ensureSwiperLoaded();
		if ( ! ok ) {
			return;
		}

		// Drop instances whose elements were detached from the DOM.
		Array.from( sliderMap.keys() ).forEach( ( el ) => {
			if ( ! document.contains( el ) ) {
				destroyOne( el );
			}
		} );

		sliders = Array.from(
			document.querySelectorAll( '.product-slider .swiper' )
		);

		sliders.forEach( ( el, index ) => {
			el.classList.add( `swiper-${ index }` );
			if ( ! sliderMap.has( el ) ) {
				createOne( el, 0 );
				return;
			}

			// Same root element survived the swap; cards inside swiper-wrapper
			// were replaced so destroy the stale instance and rebuild it from
			// scratch (Swiper.update() alone does not rebind navigation when
			// .swiper-button-{next,prev} were removed by innerHTML reset).
			destroyOne( el );
			createOne( el, 0 );
		} );

		requestAnimationFrame( () => {
			Array.from( sliderMap.values() ).forEach( ( s ) => {
				try {
					s.update();
				} catch ( _e ) {
					// Swiper instance might have been destroyed mid-frame; ignore.
				}
			} );
			equalizeSlidesHeight();
		} );
	}

	const setSlidersStructuredData = () => {
		const seoPromotedTab = document.querySelector( '[data-seo-promoted]' );
		let seoPromotedSlider = null;

		if ( seoPromotedTab ) {
			const seoPromotedPanelId = seoPromotedTab.getAttribute( 'aria-controls' );
			if ( seoPromotedPanelId ) {
				const seoPromotedPanel = document.getElementById( seoPromotedPanelId );
				if ( seoPromotedPanel ) {
					seoPromotedSlider = seoPromotedPanel.querySelector( '.product-slider .swiper' );
				}
			}
		}

		if ( !seoPromotedSlider ) {
			seoPromotedSlider = document.querySelector( '.product-slider .swiper' );
		}
		if ( !seoPromotedSlider ) {
			return;
		}

		const cards = seoPromotedSlider.querySelectorAll( '.swiper-slide' );
		const itemListElement = [];
		const productsByCode = Object.values( window.colibri?.index?.products || {} ).reduce( ( acc, product ) => {
			const code = product?.first_variant?.product_code;
			if ( code ) {
				acc[ String( code ) ] = product;
			}
			return acc;
		}, {} );
		const providerName = window.colibri?.analytics?.[ 0 ]?.brand || '';

		cards.forEach( ( cardEl, cardIndex ) => {
			const productCode = cardEl.id.replace( 'product-', '' );
			const cardTitleEl = cardEl.querySelector( '.card-title' );
			const cardTitle = cardTitleEl ? cardTitleEl.textContent.trim() : '';

			const packageDescClasses = [ '.package-items-text', '.package-items', '.product-description' ];
			let cardDescEl = null;
			for ( const packageDescClass of packageDescClasses ) {
				cardDescEl = cardEl.querySelector( packageDescClass );
				if ( cardDescEl ) {
					break;
				}
			}
			const cardDesc = cardDescEl ? cardDescEl.textContent.trim() : '';
			const cardLinkEl = cardEl.querySelector( '.details-page-link' );
			const cardLink = cardLinkEl ? cardLinkEl.getAttribute( 'href' ) : '';

			let cardPrice = cardEl.querySelector( '.discounted-price .price-value' );
			if ( !cardPrice ) {
				cardPrice = cardEl.querySelector( '.regular-price .price-value' );
			}
			let cardPriceValue = '';
			if ( cardPrice ) {
				cardPriceValue = cardPrice.textContent;
			}

			let offersCategory = 'Paid';
			if ( cardPriceValue !== '' && cardPriceValue == 0 ) {
				offersCategory = 'Free';
			}

			const matchedProduct = productsByCode[ String( productCode ) ];
			const firstVariant = matchedProduct?.first_variant;
			let courseWorkload = Number( firstVariant?.primary_hours?.[ 0 ] );

			if ( Number.isFinite( courseWorkload ) && courseWorkload > 0 ) {
				courseWorkload = `PT${courseWorkload}H`;
			} else {
				courseWorkload = '';
			}

			const courseItem = {
				'@type': 'Course',
				url: cardLink,
				name: cardTitle,
				description: cardDesc,
				provider: {
					'@type': 'Organization',
					name: providerName,
					sameAs: window.location.origin,
				},
				offers: {
					'@type': 'Offer',
					category: offersCategory,
					price: cardPriceValue,
					priceCurrency: 'USD',
				},
			};

			if ( courseWorkload ) {
				courseItem.hasCourseInstance = [
					{
						'@type': 'CourseInstance',
						courseMode: 'Blended',
						courseWorkload,
					},
				];
			}

			itemListElement.push( {
				'@type': 'ListItem',
				position: cardIndex + 1,
				item: courseItem,
			} );
		} );

		if ( !itemListElement.length ) {
			return;
		}

		const structuredDataText = JSON.stringify( {
			'@context': 'https://schema.org',
			'@type': 'ItemList',
			itemListElement,
		} );

		const script = document.createElement( 'script' );
		script.setAttribute( 'type', 'application/ld+json' );
		script.textContent = structuredDataText;
		document.head.appendChild( script );
	};

	const equalizeSlidesHeight = () => {
		const targetElements = [ '.card-title', '.pricing-container', '.package-items-text-intro' ];

		const getPreciseHeight = ( el ) => {
			el.style.minHeight = '';
			const rect = el.getBoundingClientRect();
			return Math.floor( rect.height );
		};

		const getMaxHeight = ( elements ) => {
			let maxHeight = 0;
			elements.forEach( ( el ) => {
				const h = getPreciseHeight( el );
				if ( h > maxHeight ) {
					maxHeight = h;
				}
			} );
			return maxHeight;
		};

		const setMinHeight = ( elements, height ) => {
			elements.forEach( ( el ) => {
				el.style.minHeight = `${height}px`;
			} );
		};

		const equalizeHeightsForClass = ( selector ) => {
			const elements = document.querySelectorAll( `.swiper-slide ${selector}` );
			if ( !elements.length ) {
				return;
			}
			let maxHeight = getMaxHeight( elements );
			if ( selector === '.card-title' ) {
				maxHeight = Math.max( 0, maxHeight - 1 );
			}
			setMinHeight( elements, maxHeight );
		};

		targetElements.forEach( ( selector ) => {
			equalizeHeightsForClass( selector );
		} );
	};

	const observePriceClassChanges = () => {
		const priceElements = document.querySelectorAll( '.swiper-slide .price' );
		priceElements.forEach( ( priceElement ) => {
			const observer = new MutationObserver( () => {
				if ( priceElement.classList.contains( 'has-affirm' ) ) {
					equalizeSlidesHeight();
				}
			} );
			observer.observe( priceElement, {
				attributes: true,
				attributeFilter: [ 'class' ],
			} );
		} );
	};

	const updateSliders = () => {
		Array.from( sliderMap.values() ).forEach( ( swiper ) => swiper.update() );
	};

	const filterSlides = ( _event, data ) => {
		const codes = data?.showPackages ? data.showPackages.split( ',' ) : [];
		if ( !codes.length || codes[0] === 'all' ) {
			let changed = 0;
			sliders.forEach( ( sliderEl ) => {
				sliderEl.querySelectorAll( '.card--product-slide' ).forEach( ( card ) => {
					if ( !isVisible( card ) ) {
						setVisible( card, true );
						changed += 1;
					}
				} );
			} );
			if ( changed ) {
				updateSliders();
			}
			return;
		}

		let countFlipped = 0;
		sliders.forEach( ( sliderEl ) => {
			sliderEl.querySelectorAll( '.card--product-slide' ).forEach( ( card ) => {
				const productCode = ( card.getAttribute( 'id' ) || '' )
					.replace( 'product-', '' )
					.replace( 'course-', '' );
				if ( codes.indexOf( productCode ) === -1 ) {
					if ( isVisible( card ) ) {
						setVisible( card, false );
						countFlipped += 1;
					}
				} else if ( !isVisible( card ) ) {
					setVisible( card, true );
					countFlipped += 1;
				}
			} );
		} );

		if ( countFlipped ) {
			updateSliders();
		}
	};

	const listenForFilterRequests = () => {
		const eventName = 'COLIBRI::PACKAGE_LICENSE_LEVEL_TRANSITION_FILTER';
		if ( typeof window?.colibri?.eventsQueue?.[eventName] !== 'undefined' ) {
			filterSlides( eventName, window.colibri.eventsQueue[eventName] );
		}
		window.addEventListener( eventName, ( event ) => {
			filterSlides( eventName, event.detail );
		} );
	};

	const startAll = () => {
		initSliders();
		listenForFilterRequests();
		setSlidersStructuredData();
		equalizeSlidesHeight();
		observePriceClassChanges();
	};

	const boot = async () => {
		// Bind the cross-module event listener regardless of whether sliders
		// exist at boot — ProductQuery (or another AJAX caller) may inject
		// `.product-slider` markup later via filter-change / page-change.
		document.addEventListener( 'colibri:products-updated', () => {
			refreshSliders();
		} );

		if ( ! sliders.length ) {
			return;
		}

		const ok = await ensureSwiperLoaded();
		if ( ! ok ) {
			return;
		}

		startAll();

		const onResize = debounce( () => {
			recreateSliders();
		}, 150 );

		window.addEventListener( 'resize', onResize );
		window.addEventListener( 'orientationchange', onResize );

		if ( mqMobile.addEventListener ) {
			mqMobile.addEventListener( 'change', onResize );
		} else if ( mqMobile.addListener ) {
			mqMobile.addListener( onResize );
		}
	};

	boot();
} )();
